import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  Package,
  Tag,
  AlertTriangle,
  TrendingDown,
  X
} from 'lucide-react';
import { getComercioId } from '../config/comercio.js';
import ProductoModal from '../components/ProductoModal';
import usePopup from '../hooks/usePopup';

const StockBajo = () => {
  console.log('🚀 [STOCK_BAJO] Componente montado');
  
  const [productos, setProductos] = useState([]);
  const [filteredProductos, setFilteredProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  // Estados para modales (igual que Productos.jsx)
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Hook de popup para confirmaciones
  const { toast, showSuccess, showError, showWarning, showInfo, hideToast, showConfirm, handleConfirm, handleCancel } = usePopup();

  useEffect(() => {
    console.log('🔄 [STOCK_BAJO] useEffect ejecutado');
    cargarProductosStockBajo();
  }, []);

  const cargarProductosStockBajo = async () => {
    try {
      console.log('🔄 [STOCK_BAJO] Iniciando carga de productos...');
      setLoading(true);
      
      // Obtener token de URL o localStorage
      const token = localStorage.getItem('comercio_token') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No se encontró token de comercio');
      }
      
      console.log('🔍 [STOCK_BAJO] Usando token:', token);
      const url = `https://www.sinfilas.com.ar/sf3/ventas/api/productos_stock_bajo.php?token=${token}`;
      console.log('🌐 [STOCK_BAJO] URL de API:', url);
      
      const response = await fetch(url);
      console.log('📡 [STOCK_BAJO] Respuesta HTTP:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error('Error al cargar productos con stock bajo');
      }
      
      const data = await response.json();
      console.log('📊 [STOCK_BAJO] Datos recibidos:', data);
      
      if (data.success) {
        setProductos(data.productos);
        setFilteredProductos(data.productos);
      } else {
        setError(data.message || 'Error al cargar productos');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error de conexión al servidor');
    } finally {
      setLoading(false);
    }
  };

  // Función de búsqueda
  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.trim() === '') {
      setFilteredProductos(productos);
    } else {
      const filtered = productos.filter(producto =>
        producto.nombre.toLowerCase().includes(term.toLowerCase()) ||
        producto.codigo_personalizado.toLowerCase().includes(term.toLowerCase()) ||
        producto.marca.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredProductos(filtered);
    }
  };

  // Función de ordenamiento
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key: prevConfig.key === key ? key : key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Obtener productos filtrados y ordenados
  const getFilteredAndSortedProductos = () => {
    if (!sortConfig.key) return filteredProductos;

    return [...filteredProductos].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Manejar casos especiales
      if (sortConfig.key === 'estado') {
        aValue = getStockStatus(a.stock_actual, a.stock_minimo).text;
        bValue = getStockStatus(b.stock_actual, b.stock_minimo).text;
      }

      // Convertir a string para comparación
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Obtener icono de ordenamiento
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <span className="sort-icon inactive">↕</span>;
    }
    return sortConfig.direction === 'asc' ? 
      <span className="sort-icon active">↑</span> : 
      <span className="sort-icon active">↓</span>;
  };

  // Obtener estado del stock
  const getStockStatus = (stockActual, stockMinimo) => {
    const stock = parseInt(stockActual) || 0;
    const minimo = parseInt(stockMinimo) || 0;
    
    if (stock === 0) {
      return { text: 'AGOTADO', class: 'stock-agotado', color: 'text-red-600', bgColor: 'bg-red-100' };
    }
    if (stock < minimo) {
      return { text: 'STOCK BAJO', class: 'stock-bajo', color: 'text-orange-600', bgColor: 'bg-orange-100' };
    }
    return { text: 'NORMAL', class: 'stock-normal', color: 'text-green-600', bgColor: 'bg-green-100' };
  };

  // Formatear precio
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price || 0);
  };

  // Eliminar producto
  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este producto?')) {
      try {
        // Obtener token de URL o localStorage
        const token = localStorage.getItem('comercio_token') || localStorage.getItem('token');
        
        if (!token) {
          throw new Error('No se encontró token de comercio');
        }
        
        const response = await fetch(`https://www.sinfilas.com.ar/sf3/ventas/api/productos_comercio_eliminar.php?token=${token}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id }),
        });

        if (response.ok) {
          cargarProductosStockBajo();
        } else {
          alert('Error al eliminar el producto');
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Error de conexión');
      }
    }
  };

  // Ver detalles del producto (igual que Productos.jsx)
  const handleShowDetail = (producto) => {
    setSelectedProduct(producto);
    setShowDetailModal(true);
  };

  // Editar producto (igual que Productos.jsx)
  const handleEdit = (producto) => {
    setSelectedProduct(producto);
    setShowModal(true);
  };

  // Función para manejar cuando se agrega/edita un producto
  const handleProductAdded = (action, producto) => {
    console.log('✅ [STOCK_BAJO] Producto actualizado exitosamente', { action, producto });
    showSuccess('Producto actualizado correctamente');
    
    // Actualizar el selectedProduct con los nuevos datos
    if (action === 'edit' && selectedProduct && selectedProduct.id === producto.id) {
      setSelectedProduct(producto);
      console.log('🔄 [STOCK_BAJO] Actualizando selectedProduct con nuevos datos:', producto);
    }
    
    cargarProductosStockBajo();
  };

  // Valorización del inventario
  const handleValorizacion = (producto) => {
    console.log('Valorización:', producto);
    // Mostrar popup de valorización
    setSelectedProduct(producto);
    setShowDetailModal(true);
  };

  if (loading) {
    console.log('🔄 [STOCK_BAJO] Mostrando loading...');
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-xl text-gray-700 font-medium">Cargando productos con stock bajo...</div>
        </div>
      </div>
    );
  }

  if (error) {
    console.log('❌ [STOCK_BAJO] Mostrando error:', error);
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-red-800 mb-4">Error al cargar productos</h1>
          <p className="text-red-600 text-lg">{error}</p>
          <button 
            onClick={() => cargarProductosStockBajo()}
            className="mt-6 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  console.log('🎯 [STOCK_BAJO] Renderizando componente principal');
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="page-header-stock-bajo rounded-2xl shadow-lg mb-8 animate-slide-in">
          {/* Título y controles en una sola línea */}
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Título y descripción */}
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-2 title-stock-bajo">
                📉 Stock Bajo
              </h1>
              <p className="text-gray-600 text-lg subtitle-stock-bajo">
                Productos que requieren atención inmediata
              </p>
            </div>
            
            {/* Controles principales: Buscador */}
            <div className="search-container-stock-bajo">
              {/* Buscador con botón de búsqueda */}
              <div className="search-wrapper-stock-bajo">
                <div className="search-input-wrapper relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="🔍 Buscar..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="search-stock-bajo pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm w-48"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilteredProductos(productos);
                      }}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors duration-200 p-1 rounded-full hover:bg-red-50"
                      title="Limpiar búsqueda"
                    >
                      ✕
                    </button>
                  )}
                </div>
                
                {/* Botón de Búsqueda */}
                <button
                  onClick={() => {
                    if (searchTerm.trim()) {
                      // Forzar actualización de la búsqueda
                      const filtered = productos.filter(producto =>
                        producto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        producto.codigo_personalizado.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        producto.marca.toLowerCase().includes(searchTerm.toLowerCase())
                      );
                      setFilteredProductos(filtered);
                    }
                  }}
                  className="btn-search-stock-bajo btn-search-compact px-4 py-2 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 bg-gradient-to-r from-red-500 to-orange-600 text-white hover:from-red-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!searchTerm.trim()}
                  title={searchTerm.trim() ? "Ejecutar búsqueda" : "Escribe algo para buscar"}
                >
                  <Search className="w-4 h-4" />
                  <span>Buscar</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards - Layout Horizontal en Línea */}
        <div className="stats-stock-bajo rounded-2xl shadow-lg mb-8">
          <div className="stats-horizontal-stock-bajo">
            {/* Total de productos con stock bajo */}
            <div className="stats-compact-stock-bajo stats-red flex flex-col items-center justify-center space-y-3 p-4 rounded-xl border text-center">
              <div className="icon-container p-2 bg-red-500 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div className="text-content text-center">
                <p className="text-sm font-medium text-red-700">Stock Bajo</p>
                <p className="text-2xl font-bold text-red-900">
                  {filteredProductos.length}
                </p>
              </div>
            </div>

            {/* Productos agotados */}
            <div className="stats-compact-stock-bajo stats-orange flex flex-col items-center justify-center space-y-3 p-4 rounded-xl border text-center">
              <div className="icon-container p-2 bg-orange-500 rounded-lg">
                <TrendingDown className="w-5 h-5 text-white" />
              </div>
              <div className="text-content text-center">
                <p className="text-sm font-medium text-orange-700">Críticos</p>
                <p className="text-2xl font-bold text-orange-900">
                  {filteredProductos.filter(p => parseInt(p.stock_actual) === 0).length}
                </p>
              </div>
            </div>

            {/* Valor total del stock bajo */}
            <div className="stats-compact-stock-bajo stats-purple flex flex-col items-center justify-center space-y-3 p-4 rounded-xl border text-center">
              <div className="icon-container p-2 bg-purple-500 rounded-lg">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div className="text-content text-center">
                <p className="text-sm font-medium text-purple-700">Valor Total</p>
                <p className="text-2xl font-bold text-purple-900">
                  {formatPrice(filteredProductos.reduce((sum, p) => sum + ((p.precio_venta || 0) * (p.stock_actual || 0)), 0))}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Products Table - Tabla Profesional */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header de la tabla */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 title-stock-bajo">Lista de Productos con Stock Bajo</h3>
                <p className="text-sm text-gray-600 text-wrap-safe">
                  Mostrando {getFilteredAndSortedProductos().length} productos que requieren reposición
                </p>
              </div>
            </div>
          </div>

          {/* Vista de escritorio - Tabla */}
          <div className="desktop-table-view overflow-x-auto">
            <table className="w-full products-table">
              <thead className="bg-gradient-to-r from-red-600 to-orange-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider w-1/4">
                    Producto
                  </th>
                  <th 
                    className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider w-1/6 cursor-pointer hover:bg-red-700 transition-colors duration-200"
                    onClick={() => handleSort('codigo_personalizado')}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Código</span>
                      <span className="text-lg">{getSortIcon('codigo_personalizado')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-3 py-4 text-left text-sm font-semibold uppercase tracking-wider w-1/8 cursor-pointer hover:bg-red-700 transition-colors duration-200"
                    onClick={() => handleSort('categoria')}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Categoría</span>
                      <span className="text-lg">{getSortIcon('categoria')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-3 py-4 text-left text-sm font-semibold uppercase tracking-wider w-1/8 cursor-pointer hover:bg-red-700 transition-colors duration-200"
                    onClick={() => handleSort('clasificacion_especial_nombre')}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Dietética</span>
                      <span className="text-lg">{getSortIcon('clasificacion_especial_nombre')}</span>
                    </div>
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider w-1/8">
                    Precio
                  </th>
                  <th className="px-3 py-4 text-center text-sm font-semibold uppercase tracking-wider w-1/8">
                    Stock
                  </th>
                  <th 
                    className="px-3 py-4 text-center text-sm font-semibold uppercase tracking-wider w-1/8 cursor-pointer hover:bg-red-700 transition-colors duration-200"
                    onClick={() => handleSort('estado')}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>Estado</span>
                      <span className="text-lg">{getSortIcon('estado')}</span>
                    </div>
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-semibold uppercase tracking-wider w-1/8">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getFilteredAndSortedProductos().map((producto) => {
                  const status = getStockStatus(producto.stock_actual, producto.stock_minimo);
                  
                  return (
                    <tr key={producto.id} className="hover:bg-gray-50 transition-colors duration-200">
                      {/* Columna Producto */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="product-image-container">
                            {producto.imagen_url && producto.imagen_url.trim() !== '' ? (
                              <img
                                src={producto.imagen_url}
                                alt={producto.nombre}
                                className="product-image"
                                onError={(e) => {
                                  if (e.target && e.target.style) {
                                    e.target.style.display = 'none';
                                  }
                                  if (e.target && e.target.nextSibling && e.target.nextSibling.style) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : (
                              <div className="product-image-placeholder">
                                <Package className="w-4 h-4 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {producto.nombre}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {producto.marca || 'Sin marca'}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Columna Código */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">
                          {producto.codigo_personalizado}
                        </div>
                      </td>

                      {/* Columna Categoría */}
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{producto.categoria || 'Sin categoría'}</div>
                      </td>

                      {/* Columna Categoría Dietética */}
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          {producto.clasificacion_especial_nombre ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                              {producto.clasificacion_especial_nombre}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">Sin clasificar</span>
                          )}
                        </div>
                      </td>

                      {/* Columna Precio */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatPrice(producto.precio_venta)}
                        </div>
                      </td>

                      {/* Columna Stock */}
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                        <div className="flex flex-col items-center">
                          <span className={`text-sm font-bold ${status.color}`}>
                            {producto.stock_actual}
                          </span>
                          <span className="text-xs text-gray-500">
                            Mín: {producto.stock_minimo}
                          </span>
                        </div>
                      </td>

                      {/* Columna Estado */}
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${status.bgColor} ${status.color}`}>
                          {status.text}
                        </span>
                      </td>

                      {/* Columna Acciones */}
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handleShowDetail(producto)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(producto)}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="Editar producto"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleValorizacion(producto)}
                            className="text-yellow-600 hover:text-yellow-800 transition-colors"
                            title="Valorización del inventario"
                          >
                            <Tag className="w-4 h-4" />
                          </button>
                          {parseInt(producto.stock_actual) === 0 && (
                            <button
                              onClick={() => handleDelete(producto.id)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                              title="Eliminar producto"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Vista móvil - Tarjetas (igual que Productos.jsx) */}
          <div className="mobile-cards-view">
            <div className="space-y-3 p-4">
              {getFilteredAndSortedProductos().map((producto) => {
                const status = getStockStatus(producto.stock_actual, producto.stock_minimo);
                
                return (
                  <div key={producto.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
                    {/* Header de la tarjeta */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {/* Imagen del producto */}
                        <div className="product-image-container-mobile">
                          {producto.imagen_url && 
                           producto.imagen_url.trim() !== '' && 
                           !producto.imagen_url.startsWith('blob:') ? (
                            <img
                              src={producto.imagen_url}
                              alt={producto.nombre}
                              className="product-image-mobile"
                              onError={(e) => {
                                if (e.target && e.target.style) {
                                  e.target.style.display = 'none';
                                }
                                if (e.target && e.target.nextSibling && e.target.nextSibling.style) {
                                  e.target.nextSibling.style.display = 'flex';
                                }
                              }}
                            />
                          ) : (
                            <div className="product-image-placeholder-mobile">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        {/* Información del producto */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">
                            {producto.nombre}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">
                            {producto.marca || 'Sin marca'}
                          </p>
                          <p className="text-xs text-gray-400 font-mono mt-1">
                            Código: {producto.codigo_personalizado || 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Estado del stock */}
                      <div className="flex-shrink-0">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.bgColor} ${status.color}`}>
                          {status.text}
                        </span>
                      </div>
                    </div>

                    {/* Información adicional */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <p className="text-xs text-gray-500">Categoría</p>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {producto.categoria || 'Sin categoría'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Dietética</p>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {producto.clasificacion_especial_nombre || 'Sin clasificación'}
                        </p>
                      </div>
                    </div>

                    {/* Stock actual y mínimo */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-4">
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Stock Actual</p>
                          <p className={`text-lg font-bold ${status.color}`}>
                            {producto.stock_actual || 0}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Stock Mínimo</p>
                          <p className="text-sm font-medium text-gray-600">
                            {producto.stock_minimo || 0}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleShowDetail(producto)}
                        className="btn-action-mobile"
                        title="Ver detalles"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(producto)}
                        className="btn-action-mobile"
                        title="Editar producto"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleValorizacion(producto)}
                        className="btn-action-mobile"
                        title="Valorización"
                      >
                        <Tag className="w-4 h-4" />
                      </button>
                      {parseInt(producto.stock_actual) === 0 && (
                        <button
                          onClick={() => handleDelete(producto.id)}
                          className="btn-action-mobile btn-danger"
                          title="Eliminar producto"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mensaje cuando no hay productos */}
          {filteredProductos.length === 0 && (
            <div className="text-center py-12 px-4">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg text-wrap-safe">No hay productos con stock bajo</p>
              <p className="text-gray-400 text-wrap-safe">¡Excelente! Todos los productos están por encima del límite de reposición</p>
            </div>
          )}
        </div>
      </div>

        {/* Popup de detalles del producto - Igual que Productos.jsx */}
        {showDetailModal && selectedProduct && (
          <div className="popup-overlay">
            <div className="product-detail-popup">
              {/* Header del popup */}
              <div className="popup-header">
                <h3>📋 {selectedProduct.nombre}</h3>
                <p>{selectedProduct.marca || 'Sin marca'}</p>
                
                {/* Botón de cerrar */}
                <button
                  className="popup-close-btn"
                  onClick={() => setShowDetailModal(false)}
                  title="Cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Contenido del popup */}
              <div className="popup-content">
                <div className="popup-grid">
                  {/* Columna izquierda - Imagen y estado */}
                  <div className="flex flex-col items-center">
                    {/* Imagen del producto */}
                    {selectedProduct.imagen_url ? (
                      <img 
                        src={selectedProduct.imagen_url} 
                        alt={selectedProduct.nombre}
                        className="popup-product-image"
                      />
                    ) : (
                      <div className="popup-product-image bg-gray-200 flex items-center justify-center">
                        <Package className="w-20 h-20 text-gray-400" />
                      </div>
                    )}
                    
                    {/* Estado del stock */}
                    <div className="text-center">
                      {(() => {
                        const stock = selectedProduct.stock_actual || 0;
                        const stockMin = selectedProduct.stock_minimo || 0;
                        
                        if (stock <= 0) {
                          return (
                            <span className="popup-status-badge bg-red-500 text-white">
                              🔴 Sin Stock
                            </span>
                          );
                        } else if (stock <= stockMin) {
                          return (
                            <span className="popup-status-badge bg-yellow-500 text-white">
                              🟡 Bajo Stock
                            </span>
                          );
                        } else {
                          return (
                            <span className="popup-status-badge bg-green-500 text-white">
                              🟢 Disponible
                            </span>
                          );
                        }
                      })()}
                    </div>
                  </div>
                  
                  {/* Columna derecha - Información organizada */}
                  <div className="space-y-2">
                    {/* Información básica */}
                    <div className="popup-section">
                      <h4>📋 Básico</h4>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="info-card">
                          <div className="label">Código</div>
                          <div className="value">
                            {selectedProduct.codigo_personalizado || 'N/A'}
                          </div>
                        </div>
                        <div className="info-card">
                          <div className="label">Categoría</div>
                          <div className="value">
                            {selectedProduct.categoria || 'General'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Precios */}
                    <div className="popup-section">
                      <h4>💰 Precios</h4>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="info-card">
                          <div className="label">Venta</div>
                          <div className="value">
                            {formatPrice(selectedProduct.precio_venta)}
                          </div>
                        </div>
                        <div className="info-card">
                          <div className="label">Compra</div>
                          <div className="value">
                            {formatPrice(selectedProduct.precio_compra)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stock */}
                    <div className="popup-section">
                      <h4>📦 Stock</h4>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="info-card">
                          <div className="label">Actual</div>
                          <div className="value">
                            {selectedProduct.stock_actual || 0}
                          </div>
                        </div>
                        <div className="info-card">
                          <div className="label">Mínimo</div>
                          <div className="value">
                            {selectedProduct.stock_minimo || 0}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Descripción */}
                    {selectedProduct.descripcion && (
                      <div className="popup-section">
                        <h4>📝 Descripción</h4>
                        <div className="info-card">
                          <p className="text-gray-700 text-xs leading-tight">
                            {selectedProduct.descripcion}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Acciones del popup */}
              <div className="popup-actions">
                <div className="actions-container">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="btn-secondary"
                    title="Cerrar popup"
                  >
                    Cerrar
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleEdit(selectedProduct);
                    }}
                    className="btn-primary"
                    title="Editar producto"
                  >
                    Editar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Producto para editar */}
        <ProductoModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedProduct(null);
            // Refrescar la lista de productos al cerrar el modal
            cargarProductosStockBajo();
          }}
          onProductAdded={handleProductAdded}
          productoExistente={selectedProduct}
        />

        {/* Toast de notificaciones */}
        {toast && (
          <div className="fixed top-4 right-4 z-50">
            <div className={`px-6 py-4 rounded-lg shadow-lg ${
              toast.type === 'success' ? 'bg-green-500 text-white' :
              toast.type === 'error' ? 'bg-red-500 text-white' :
              toast.type === 'warning' ? 'bg-yellow-500 text-white' :
              'bg-blue-500 text-white'
            }`}>
              <div className="flex items-center justify-between">
                <span>{toast.message}</span>
                <button
                  onClick={hideToast}
                  className="ml-4 text-white hover:text-gray-200"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default StockBajo;
